import type { ComponentChildren } from "preact";
import { TrashIcon } from "@heroicons/react/24/outline";
import {
  Badge,
  Button,
  Card,
  CardBody,
  IconButton,
  type BadgeTone,
} from "./ui";

export type UserListLayout = "desktop" | "mobile";

type Action<T> = {
  desktopKind: "button" | "icon";
  desktopLabel: string;
  mobileLabel: string;
  ariaLabel?: string;
  onAction: (item: T) => void | Promise<void>;
};

type Props<T> = {
  items: T[];
  getKey: (item: T) => string;
  getEmail: (item: T) => string;
  getName?: (item: T) => string | null;
  // Role column is optional: platform user rows no longer carry a user-level
  // role, while project member rows still show owner/member.
  getRole?: (item: T) => string;
  getRoleTone?: (item: T) => BadgeTone;
  getEmailVerified: (item: T) => boolean;
  renderRole?: (item: T, layout: UserListLayout) => ComponentChildren;
  extraColumn?: {
    label: string;
    render: (item: T, layout: UserListLayout) => ComponentChildren;
  };
  showMobileVerification?: boolean;
  action?: Action<T> | ((item: T) => Action<T> | undefined);
};

export function UserList<T>({
  items,
  getKey,
  getEmail,
  getName,
  getRole,
  getRoleTone,
  getEmailVerified,
  renderRole,
  extraColumn,
  showMobileVerification = false,
  action,
}: Props<T>) {
  const hasRoleColumn = Boolean(renderRole || getRole);

  function roleFor(item: T, layout: UserListLayout) {
    if (renderRole) return renderRole(item, layout);
    if (!getRole) return null;
    return <Badge tone={getRoleTone?.(item) ?? "neutral"}>{getRole(item)}</Badge>;
  }

  function actionFor(item: T): Action<T> | undefined {
    return typeof action === "function" ? action(item) : action;
  }

  return (
    <>
      {/* Desktop / tablet — table layout */}
      <Card class="hidden overflow-hidden sm:block">
        <table class="w-full text-left text-sm">
          <thead class="border-b border-border-subtle text-xs font-medium text-fg-subtle">
            <tr>
              <th class="px-5 py-3">メール</th>
              {hasRoleColumn && <th class="px-5 py-3">役割</th>}
              {extraColumn && <th class="px-5 py-3">{extraColumn.label}</th>}
              <th class="px-5 py-3">検証済</th>
              <th class="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const email = getEmail(item);
              const name = getName?.(item);
              const itemAction = actionFor(item);
              return (
                <tr key={getKey(item)} class="border-t border-border-subtle">
                  <td
                    class={`px-5 py-3 ${getName ? "" : "font-mono text-fg"}`}
                  >
                    {getName ? (
                      <>
                        <div class="font-mono text-fg">{email}</div>
                        {name && (
                          <div class="text-xs text-fg-subtle">{name}</div>
                        )}
                      </>
                    ) : (
                      email
                    )}
                  </td>
                  {hasRoleColumn && (
                    <td class="px-5 py-3">{roleFor(item, "desktop")}</td>
                  )}
                  {extraColumn && (
                    <td class="px-5 py-3">
                      {extraColumn.render(item, "desktop")}
                    </td>
                  )}
                  <td class="px-5 py-3 text-xs text-fg-muted">
                    {getEmailVerified(item) ? (
                      "✓"
                    ) : (
                      <span class="text-fg-faint">未</span>
                    )}
                  </td>
                  <td class="px-5 py-3 text-right">
                    {itemAction?.desktopKind === "icon" ? (
                      <IconButton
                        variant="ghost"
                        ariaLabel={
                          itemAction.ariaLabel ?? itemAction.desktopLabel
                        }
                        onClick={() => itemAction.onAction(item)}
                      >
                        <TrashIcon class="h-4 w-4" />
                      </IconButton>
                    ) : itemAction ? (
                      <Button
                        size="sm"
                        variant="danger"
                        iconLeft={<TrashIcon class="h-3.5 w-3.5" />}
                        onClick={() => itemAction.onAction(item)}
                      >
                        {itemAction.desktopLabel}
                      </Button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* Mobile (≤640px) — card list layout */}
      <ul class="grid gap-2 sm:hidden">
        {items.map((item) => {
          const email = getEmail(item);
          const name = getName?.(item);
          const itemAction = actionFor(item);
          return (
            <li key={getKey(item)}>
              <Card>
                <CardBody class="space-y-3">
                  <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0 space-y-0.5">
                      <div class="truncate font-mono text-sm text-fg">
                        {email}
                      </div>
                      {name && (
                        <div class="text-xs text-fg-subtle">{name}</div>
                      )}
                      {extraColumn?.render(item, "mobile")}
                    </div>
                    {roleFor(item, "mobile")}
                  </div>
                  {(showMobileVerification || itemAction) && (
                    <div
                      class={
                        showMobileVerification
                          ? "flex items-center justify-between text-xs text-fg-subtle"
                          : "flex items-center justify-end"
                      }
                    >
                      {showMobileVerification && (
                        <span>
                          {getEmailVerified(item) ? "検証済み" : "未検証"}
                        </span>
                      )}
                      {itemAction && (
                        <Button
                          size="sm"
                          variant="danger"
                          iconLeft={<TrashIcon class="h-3.5 w-3.5" />}
                          onClick={() => itemAction.onAction(item)}
                        >
                          {itemAction.mobileLabel}
                        </Button>
                      )}
                    </div>
                  )}
                </CardBody>
              </Card>
            </li>
          );
        })}
      </ul>
    </>
  );
}
